import { useCallback, useEffect, useState } from 'react';
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineGrid,
  InlineStack,
  Banner,
  Badge,
  SkeletonBodyText,
  Button,
  Icon,
  Box,
  Divider,
} from '@shopify/polaris';
import { CheckCircleIcon } from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { AnalyticsDashboard } from '@freebi/shared';
import { useApi } from '../providers/ApiProvider';
import { useStore } from '../providers/StoreProvider';
import { openShopifyAdminUrl } from '../lib/shopifyNav';
import { themeEmbedDeepLink } from '../lib/shopContext';
import { StatCard } from '../components/StatCard';
import { AdminLanguageMenu } from '../components/AdminLanguageMenu';
import { t } from '../i18n';

/** API sends “Hi {name} 👋” — translate the Hi, keep the merchant name. */
function formatDashboardGreeting(raw?: string): string {
  if (!raw) return t('Dashboard');
  const named = raw.match(/^Hi\s+(.+?)\s*👋\s*$/i);
  if (named) return t('Hi {{name}} 👋', { name: named[1] });
  return t(raw);
}

interface SetupStep {
  id: string;
  title: string;
  description: string;
  done: boolean;
  actionLabel?: string;
  actionHref?: string;
}

interface StoreSetupStatus {
  ownerFirstName: string;
  ownerLastName?: string;
  ownerName?: string;
  shopName: string;
  shopDomain: string;
  greeting: string;
  welcomeLine: string;
  appEmbed: {
    active: boolean;
    statusLabel: 'Active' | 'Inactive';
    message: string;
    deepLink: string;
    blockName?: string;
    source?: 'theme' | 'heartbeat' | 'cache' | 'unknown';
  };
  guide: {
    dismissed: boolean;
    steps: SetupStep[];
    completedCount: number;
    totalCount: number;
  };
  onboardingDone: boolean;
}

/** Poll faster while embed is inactive so status flips soon after theme Save */
const POLL_MS_INACTIVE = 4000;
const POLL_MS_ACTIVE = 20000;

export function DashboardPage() {
  const api = useApi();
  const navigate = useNavigate();
  const { formatMoney } = useStore();
  const [data, setData] = useState<AnalyticsDashboard | null>(null);
  const [setup, setSetup] = useState<StoreSetupStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [embedBusy, setEmbedBusy] = useState(false);

  const loadSetup = useCallback(async () => {
    try {
      const s = await api.get<StoreSetupStatus>('/stores/setup');
      setSetup(s);
      return s;
    } catch {
      // Fallback so UI still renders in offline/demo edge cases
      const fallback: StoreSetupStatus = {
        ownerFirstName: 'there',
        ownerLastName: '',
        ownerName: 'there',
        shopName: 'your store',
        shopDomain: '',
        greeting: 'Hi there 👋',
        welcomeLine: 'Welcome to Freebi',
        appEmbed: {
          active: false,
          statusLabel: 'Inactive',
          message:
            "Freebi is not activated on your store yet. Customers won't see free gifts until you activate it.",
          deepLink: '#',
        },
        guide: {
          dismissed: false,
          steps: [
            {
              id: 'activate_app',
              title: 'Activate app',
              description: 'Set up the essential settings to activate your app.',
              done: false,
              actionLabel: 'Activate App',
            },
            {
              id: 'create_campaign',
              title: 'Create your first campaign',
              description: 'Launch a free gift, BOGO, or cart goal campaign.',
              done: false,
              actionLabel: 'Create campaign',
              actionHref: '/campaigns/new',
            },
            {
              id: 'customize_widget',
              title: 'Customize your widget',
              description: 'Style your progress bar or gift selector.',
              done: false,
              actionLabel: 'Customize widget',
              actionHref: '/widgets/popup',
            },
          ],
          completedCount: 0,
          totalCount: 3,
        },
        onboardingDone: false,
      };
      setSetup(fallback);
      return fallback;
    }
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await loadSetup();
      try {
        const dash = await api.get<AnalyticsDashboard>('/analytics/dashboard');
        if (!cancelled) setData(dash);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, loadSetup]);

  // Poll activation status + guide progress (faster until embed is active)
  useEffect(() => {
    const ms =
      setup?.appEmbed?.active === true ? POLL_MS_ACTIVE : POLL_MS_INACTIVE;
    const id = window.setInterval(() => {
      void loadSetup();
    }, ms);

    const onFocus = () => {
      void loadSetup();
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') void loadSetup();
    };
    const onPageShow = () => {
      void loadSetup();
    };

    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [loadSetup, setup?.appEmbed?.active]);

  const fmt = (n: number) => formatMoney(n, { maximumFractionDigits: 0 });

  async function openAppEmbedActivator() {
    if (!setup) return;
    setEmbedBusy(true);
    try {
      const url = themeEmbedDeepLink(
        setup.appEmbed.deepLink,
        setup.shopDomain,
      );
      if (url) {
        openShopifyAdminUrl(url, { newTab: true });
      }
      // Re-check repeatedly after theme editor (Save can take a few seconds to sync)
      [3, 6, 10, 15, 25].forEach((sec) => {
        window.setTimeout(() => void loadSetup(), sec * 1000);
      });
    } finally {
      setEmbedBusy(false);
    }
  }

  function onGuideAction(step: SetupStep) {
    if (step.id === 'activate_app') {
      void openAppEmbedActivator();
      return;
    }
    if (step.id === 'customize_widget') {
      navigate('/widgets/popup');
      return;
    }
    if (step.actionHref?.startsWith('http')) {
      window.open(step.actionHref, '_blank', 'noopener,noreferrer');
      return;
    }
    if (step.actionHref) {
      navigate(step.actionHref);
    }
  }

  return (
    <Page>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="start" wrap gap="400">
          <BlockStack gap="100">
            <Text as="h1" variant="headingLg">
              {formatDashboardGreeting(setup?.greeting)}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {t(setup?.welcomeLine || 'Welcome to Freebi')}
            </Text>
          </BlockStack>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 10,
            }}
          >
            <AdminLanguageMenu />
            <Button
              variant="primary"
              onClick={() => navigate('/campaigns/new')}
            >
              {t('Create campaign')}
            </Button>
          </div>
        </InlineStack>
        {/* Theme embed status — always visible (active or not) */}
        {setup && (
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center" wrap>
                <InlineStack gap="200" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    {t("Storefront widget")}
                  </Text>
                  <Badge tone={setup.appEmbed.active ? 'success' : 'warning'}>
                    {setup.appEmbed.active ? 'Active' : 'Inactive'}
                  </Badge>
                </InlineStack>
                <InlineStack gap="200" wrap>
                  <Button
                    size="slim"
                    loading={embedBusy}
                    onClick={() => {
                      setEmbedBusy(true);
                      void loadSetup().finally(() => setEmbedBusy(false));
                    }}
                  >
                    {t("Refresh status")}
                  </Button>
                  <a
                    href={
                      themeEmbedDeepLink(
                        setup.appEmbed.deepLink,
                        setup.shopDomain,
                      ) || '#'
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      const url = themeEmbedDeepLink(
                        setup.appEmbed.deepLink,
                        setup.shopDomain,
                      );
                      if (!url) {
                        e.preventDefault();
                        return;
                      }
                      // Do not rely on App Bridge window.open — it keeps admin
                      // navigations in the same window. Use our new-tab helper
                      // while still preventing iframe same-window navigation.
                      e.preventDefault();
                      e.stopPropagation();
                      openShopifyAdminUrl(url, { newTab: true });
                      [3, 6, 10, 15, 25].forEach((sec) => {
                        window.setTimeout(() => void loadSetup(), sec * 1000);
                      });
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '6px 16px',
                      borderRadius: '8px',
                      background: setup.appEmbed.active ? '#e3e3e3' : '#303030',
                      color: setup.appEmbed.active ? '#303030' : '#fff',
                      textDecoration: 'none',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: 'none',
                    }}
                  >
                    {setup.appEmbed.active
                      ? 'Open theme editor'
                      : 'Activate app'}
                  </a>
                </InlineStack>
              </InlineStack>
              <Text as="p" tone="subdued">
                {t(
                  setup.appEmbed.message ||
                    (setup.appEmbed.active
                      ? 'Freebi is active on your store. Customers can see gifts and offers.'
                      : "Freebi is not activated on your store yet. Customers won't see free gifts until you activate it."),
                )}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {setup.appEmbed.active ? (
                  <>
                    {t("Manage the")}{' '}
                    <Text as="span" fontWeight="semibold">
                      {setup.appEmbed.blockName || 'Storefront gifts'}
                    </Text>{' '}
                    {t("app embed in your theme anytime.")}
                  </>
                ) : (
                  <>
                    {t("Opens the theme editor. Turn on")}{' '}
                    <Text as="span" fontWeight="semibold">
                      {setup.appEmbed.blockName || 'Storefront gifts'}
                    </Text>
                    {t(", then click")}{' '}
                    <Text as="span" fontWeight="semibold">
                      {t("Save")}
                    </Text>
                    .
                  </>
                )}
              </Text>
            </BlockStack>
          </Card>
        )}

        {/* Setup guide — always visible (steps stay checked when done) */}
        {setup && (
          <Card>
            <BlockStack gap="300">
              <BlockStack gap="100">
                <InlineStack align="space-between" blockAlign="center" wrap>
                  <Text as="h2" variant="headingMd">
                    {t("Setup guide")}
                  </Text>
                  {setup.guide.completedCount >= setup.guide.totalCount ? (
                    <Badge tone="success">{t("Complete")}</Badge>
                  ) : (
                    <Badge tone="info">{t("In progress")}</Badge>
                  )}
                </InlineStack>
                <Text as="p" tone="subdued">
                  {t("Use this guide to get Freebi up and running — steps stay\n                  here after you finish so you can revisit anytime.")}
                </Text>
              </BlockStack>

              <Text as="p" variant="bodySm" tone="subdued">
                {setup.guide.completedCount} of {setup.guide.totalCount} {t("steps\n                complete")}
              </Text>

              <BlockStack gap="200">
                {setup.guide.steps.map((step) => (
                  <Box
                    key={step.id}
                    padding="300"
                    background={
                      step.done ? 'bg-surface-secondary' : 'bg-surface'
                    }
                    borderRadius="200"
                    borderWidth="025"
                    borderColor="border"
                  >
                    <InlineStack
                      align="space-between"
                      blockAlign="center"
                      wrap
                      gap="300"
                    >
                      <InlineStack gap="300" blockAlign="start" wrap={false}>
                        <div
                          style={{ marginTop: 2, flexShrink: 0 }}
                          aria-hidden
                        >
                          {step.done ? (
                            <Icon source={CheckCircleIcon} tone="success" />
                          ) : (
                            <div
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                border: '2px solid #8c9196',
                                boxSizing: 'border-box',
                              }}
                              title={t("Not completed")}
                            />
                          )}
                        </div>
                        <BlockStack gap="050">
                          <Text
                            as="span"
                            fontWeight="semibold"
                            tone={step.done ? 'subdued' : undefined}
                          >
                            {t(step.title)}
                          </Text>
                          <Text as="p" tone="subdued" variant="bodySm">
                            {t(step.description)}
                          </Text>
                        </BlockStack>
                      </InlineStack>
                      {/* Keep actions available — reopen theme / campaigns / widgets */}
                      {step.actionHref && (
                        <Button onClick={() => onGuideAction(step)}>
                          {step.done
                            ? step.id === 'activate_app'
                              ? t('Open theme')
                              : step.id === 'create_campaign'
                                ? t('View campaigns')
                                : step.id === 'customize_widget'
                                  ? t('Edit widget')
                                  : t('Open')
                            : t(step.actionLabel || 'Continue')}
                        </Button>
                      )}
                    </InlineStack>
                  </Box>
                ))}
              </BlockStack>
            </BlockStack>
          </Card>
        )}

        {error && (
          <Banner tone="warning" title={t("Analytics preview")}>
            <p>{t("Could not fully load metrics (")}{error}{t("). Setup cards above still work.")}</p>
          </Banner>
        )}

        <Divider />

        {loading && !data ? (
          <Card>
            <SkeletonBodyText lines={6} />
          </Card>
        ) : (
          <>
            <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
              <StatCard
                title={t("Revenue generated")}
                value={fmt(data?.stats.revenueGenerated ?? 0)}
                hint="Attributed to Freebi offers"
              />
              <StatCard
                title={t("AOV increase")}
                value={`+${(data?.stats.aovIncreasePercent ?? 0).toFixed(1)}%`}
                hint={`≈ ${fmt(data?.stats.aovIncrease ?? 0)} per order`}
              />
              <StatCard
                title={t("Conversion rate")}
                value={`${(data?.stats.conversionRate ?? 0).toFixed(1)}%`}
                hint={`${data?.stats.ordersInfluenced ?? 0} orders influenced`}
              />
              <StatCard
                title={t("Gift redemption")}
                value={String(data?.stats.giftRedemption ?? 0)}
                hint={`${(data?.stats.giftRedemptionRate ?? 0).toFixed(0)}% acceptance`}
              />
            </InlineGrid>

            <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
              <StatCard
                title={t("Impressions")}
                value={(data?.stats.campaignImpressions ?? 0).toLocaleString()}
              />
              <StatCard
                title={t("Click rate")}
                value={`${(data?.stats.clickRate ?? 0).toFixed(1)}%`}
              />
              <StatCard
                title={t("Active campaigns")}
                value={String(data?.stats.activeCampaigns ?? 0)}
              />
              <StatCard
                title={t("Orders influenced")}
                value={(data?.stats.ordersInfluenced ?? 0).toLocaleString()}
              />
            </InlineGrid>

            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  {t("Revenue trends (30 days)")}
                </Text>
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <LineChart data={data?.trends || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        stroke="#008060"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  {t("Top performing campaigns")}
                </Text>
                {(data?.topCampaigns || []).map((c) => (
                  <div
                    key={c.campaignId}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: '1px solid #e1e3e5',
                    }}
                  >
                    <BlockStack gap="100">
                      <Text as="span" fontWeight="semibold">
                        {c.campaignName}
                      </Text>
                      <Badge>{c.type}</Badge>
                    </BlockStack>
                    <BlockStack gap="100" inlineAlign="end">
                      <Text as="span">{fmt(c.revenue)}</Text>
                      <Text as="span" tone="subdued" variant="bodySm">
                        {c.conversionRate.toFixed(1)}{t("% CVR ·")} {c.conversions} orders
                      </Text>
                    </BlockStack>
                  </div>
                ))}
                <Button onClick={() => navigate('/campaigns')}>{t("View all campaigns")}</Button>
              </BlockStack>
            </Card>
          </>
        )}
      </BlockStack>
    </Page>
  );
}
